import React, { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import {
  Link,
  MessageBar,
  MessageBarBody,
  mergeClasses,
  Text,
  Title1,
  tokens,
} from '@fluentui/react-components';

import { useUserInvitationSelectionPageStyles } from './use-user-invitation-selection-page-styles';
import { IdpButtonList, type IdpButtonListProps } from '../idp-button-list';

const AAD_ONLY_PROVIDERS: IdpButtonListProps['providers'] = ['MSA'];

// "Signing in with a work account" help article. The locale segment is replaced at runtime with
// the current UI locale from server context (e.g. en-US -> "en").
const WORK_ACCOUNT_HELP_ARTICLE = '60043/-1';

export type InvitationAadOnlyViewProps = {
  invitationSummary?: string | null;
  daysLeftPill?: React.ReactNode;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
  // eslint-disable-next-line no-unused-vars
  onIdpSelected?: (provider: string) => void;
};

// AAD-enforced invitation: when the inviting customer disallows MSA we skip the
// create/sign-in choice page and show only the "Sign in with Microsoft" (AAD) option.
export const InvitationAadOnlyView: React.FC<InvitationAadOnlyViewProps> = ({
  invitationSummary, daysLeftPill, logger, onIdpSelected,
}) => {
  const i18n = useContext(I18nContext) as any;
  const serverContext = useContext(ServerContext) as any;
  const classes = useUserInvitationSelectionPageStyles();

  // Help center deep-links are locale-scoped (…/apex/ads/{locale}/…). Derive the language segment
  // from the server-provided CurrentLocale (e.g. "en-US" -> "en"), defaulting to English.
  const locale = (serverContext?.CurrentLocale || 'en-us').toLowerCase().split('-')[0];
  const workAccountHelpUrl = `https://help.ads.microsoft.com/#apex/ads/${locale}/${WORK_ACCOUNT_HELP_ARTICLE}`;

  return (
    <div className={classes.textAndButtons}>
      {daysLeftPill && <div className={classes.invitationMeta}>{daysLeftPill}</div>}
      <Title1 as="h1" block className={mergeClasses('idp-title', classes.title)}>
        {i18n.getString(_TL_('You\'ve been invited to join Microsoft Advertising.'))}
      </Title1>
      {invitationSummary && (
        <Text block size={300} color={tokens.colorNeutralForeground2} className="invitation-summary">
          {invitationSummary}
        </Text>
      )}
      <Text
        block
        size={300}
        color={tokens.colorNeutralForeground2}
        className="invitation-aad-only-work-account-note"
      >
        <ReactTemplate
          template={i18n.getString(_TL_('These advertising accounts require a Microsoft work or school account to sign in. {{a}}Learn More{{/a}}'))}
          model={{
            a: (text: string) => (
              <Link
                className="invitation-aad-only-learn-more-link"
                href={workAccountHelpUrl}
                target="_blank"
                rel="noopener noreferrer"
                inline
                onClick={() => logger?.trace('InvitationAadOnlyView: Clicked work-account Learn More link')}
              >
                {text}
              </Link>
            ),
          }}
        />
      </Text>
      <div className={mergeClasses('idp-selection-page-buttons', classes.buttons)}>
        <IdpButtonList
          scenario="userInvitation"
          invitationVariant="signin"
          aadOnly
          logger={logger}
          onIdpSelected={onIdpSelected}
          providers={AAD_ONLY_PROVIDERS}
        />
      </div>
      <MessageBar
        className={mergeClasses('invitation-aad-only-info', classes.aadInfoMessage)}
        intent="info"
      >
        <MessageBarBody>
          {i18n.getString(_TL_('Don\'t have a Microsoft work or school email? Contact your IT admin.'))}
        </MessageBarBody>
      </MessageBar>
    </div>
  );
};
